import { Label, RadioGroup, TextInput, Checkbox } from '@silverfort/sf-components';
import { FC, useCallback, useEffect, useState } from 'react';
import { useUserManagementSettingsContext } from '../context/UserManagementSettingsContext';
import { UserManagementActionTypes } from '../reducer/types';
import {
    MAX_TOKENS_TOOLTIP_TEXT,
    SEND_MFA_ENROLLMENT_CONFIRMATION_EMAIL_TOOLTIP_TEXT,
    UNPAIRED_USER_POLICY_GROUP_OPTIONS
} from '../consts';
import { useSnackAlert } from '../../../../UserManagement/context/SnackAlertProvider';

const IS_NUMERIC_PATTERN = /^\d+$/;

const convertBlockUnpairedUsersToValue = (BlockUnpairedUsers: boolean) => {
    if (BlockUnpairedUsers) {
        return 'deny';
    }
    return 'allow';
};

const convertValueToBlockUnpairedUsers = (value: string) => {
    if (value === 'deny') {
        return true;
    }
    return false;
};

const Section = ({ children }) => {
    return (
        <div className="rounded-[8px] border border-sf-gray-100 p-6 gap-6 flex flex-row items-center">{children}</div>
    );
};

const EnrollmentSettings: FC = () => {
    const { reducerState, dispatch, saveFunc } = useUserManagementSettingsContext();
    const { openSnackAlert } = useSnackAlert();
    const { SendNewTokenEmail, UserTokensLimit, BlockUnpairedUsers } = reducerState?.enrollmentSettings?.fields;

    const [maxAllowedTokens, setMaxAllowedTokens] = useState<number | string>(UserTokensLimit.value);
    const [maxAllowedTokensError, setMaxAllowedTokensError] = useState('');

    useEffect(() => {
        setMaxAllowedTokens(UserTokensLimit.value);
        setMaxAllowedTokensError('');
    }, [UserTokensLimit.value]);

    const updateField = useCallback(
        async (field: 'SendNewTokenEmail' | 'UserTokensLimit' | 'BlockUnpairedUsers', value: any) => {
            const payload = { [field]: value };
            dispatch({
                type: UserManagementActionTypes.UPDATE_ENROLLMENT_SETTINGS,
                payload
            });
            await saveFunc(payload, false, false);
        },
        [dispatch, saveFunc]
    );

    const updateMaxAllowedTokens = useCallback(
        (value: string) => {
            if (value === '') {
                setMaxAllowedTokens(value);
                setMaxAllowedTokensError('Please enter a number');
                return;
            }
            if (IS_NUMERIC_PATTERN.test(value)) {
                const newValue = Number(value);
                setMaxAllowedTokens(newValue);
                if (newValue < 0 || newValue > 100) {
                    setMaxAllowedTokensError('Please enter a number between 0 and 100');
                } else {
                    setMaxAllowedTokensError('');
                    updateField('UserTokensLimit', newValue);
                }
            } else {
                setMaxAllowedTokensError('Please enter a number');
            }
        },
        [setMaxAllowedTokens, setMaxAllowedTokensError, updateField]
    );

    const updateSendEnrollmentEmail = useCallback(
        (value: boolean) => {
            updateField('SendNewTokenEmail', value);
        },
        [updateField]
    );

    return (
        <div className="h-full flex flex-col justify-between">
            <div className="py-4 flex flex-col gap-4">
                <Section>
                    <Label
                        text="Maximum allowed tokens"
                        className="min-h-10 w-[235px]"
                        tooltipInfo={MAX_TOKENS_TOOLTIP_TEXT}
                    />
                    <TextInput
                        onChange={updateMaxAllowedTokens}
                        value={maxAllowedTokens}
                        validationMessage={maxAllowedTokensError}
                    />
                </Section>
                <Section>
                    <Label text="Unpaired user policy" className="min-h-10 w-[235px]" />
                    <RadioGroup
                        onChange={(value) => {
                            const blockUnpairedUsers = convertValueToBlockUnpairedUsers(value as string);
                            updateField('BlockUnpairedUsers', blockUnpairedUsers);
                            openSnackAlert({
                                message: `Unpaired users policy has changed from ${
                                    blockUnpairedUsers ? 'Allow' : 'Deny'
                                } to ${blockUnpairedUsers ? 'Deny' : 'Allow'}`
                            });
                        }}
                        options={UNPAIRED_USER_POLICY_GROUP_OPTIONS}
                        orientation={'horizontal'}
                        selectedOption={convertBlockUnpairedUsersToValue(BlockUnpairedUsers.value)}
                    />
                </Section>
                <Section>
                    <Label text="MFA confirmation email" className="min-h-10 w-[235px]" />
                    <div className="flex flex-row items-center gap-2">
                        <Checkbox
                            checked={SendNewTokenEmail.value}
                            onChange={(event) => updateSendEnrollmentEmail(event.target.checked)}
                        />
                        <Label
                            text="Send MFA enrollment confirmation email"
                            tooltipInfo={SEND_MFA_ENROLLMENT_CONFIRMATION_EMAIL_TOOLTIP_TEXT}
                        />
                    </div>
                </Section>
            </div>
        </div>
    );
};

export default EnrollmentSettings;
